import React, { CSSProperties, ReactNode, useRef } from 'react';
import { useScrollHints } from '../hooks/useScrollHints';

interface ScrollableTabStripProps {
    children: ReactNode;
    /** The strip's own styling. Layout that must change on a phone belongs in .rw-tab-strip. */
    style?: CSSProperties;
    /** The active tab's id, so the strip re-centres when the tab changes. */
    activeKey?: unknown;
}

/**
 * A row of tabs that scrolls sideways below 1024px instead of pushing the page wider, with an arrow
 * at whichever end has more tabs hidden.
 *
 * The Settings tabs (six) and the Profile and Team sub-tabs were ~640px of buttons on a 390px
 * screen -- the whole of the horizontal overflow every Settings route had.
 *
 * The arrows sit on the wrapper rather than inside the scroller: a child of the strip would scroll
 * away with the tabs instead of staying pinned to the edges. Mark the active tab with
 * `data-scroll-active="true"` and it is scrolled into view.
 */
export const ScrollableTabStrip: React.FC<ScrollableTabStripProps> = ({ children, style, activeKey }) => {
    const stripRef = useRef<HTMLDivElement>(null);
    const hints = useScrollHints(stripRef, activeKey);

    return (
        // min-width: 0 so the wrapper can shrink when it is itself a flex item -- without it the
        // strip's own `max-width: 100%` measures against a box that is still sizing to its content,
        // and the row pushes the page wider instead of scrolling.
        <div style={{ position: 'relative', minWidth: 0, maxWidth: '100%' }}>
            {hints.left && <div className="rw-scroll-hint rw-scroll-hint-tabs rw-scroll-hint-left" aria-hidden>{'‹'}</div>}
            {hints.right && <div className="rw-scroll-hint rw-scroll-hint-tabs rw-scroll-hint-right" aria-hidden>{'›'}</div>}
            <div ref={stripRef} className="rw-tab-strip" style={style}>
                {children}
            </div>
        </div>
    );
};
